import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Field, { inputClass } from '@/components/base/Field';

interface StudentOption {
  id: string;
  name: string;
}

interface QuickMakeupModalProps {
  open: boolean;
  onClose: () => void;
  students: StudentOption[];
  onAdded: () => void;
}

const REASONS = ['결석 보충', '추가 수업', '진도 보충', '시험 대비', '기타'];

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function QuickMakeupModal({
  open,
  onClose,
  students,
  onAdded,
}: QuickMakeupModalProps) {
  const [studentId, setStudentId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStudentId(students[0]?.id ?? '');
      setDate(todayStr());
      setReason(REASONS[0]);
      setNote('');
      setError('');
    }
  }, [open, students]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!studentId) {
      setError('학생을 선택해주세요.');
      return;
    }
    if (!date) {
      setError('보충 일자를 선택해주세요.');
      return;
    }
    setSaving(true);
    try {
      const { error: insertError } = await supabase.from('makeup_schedules').insert({
        student_id: studentId,
        scheduled_date: date,
        reason,
        note: note.trim(),
      });
      if (insertError) throw insertError;
      onAdded();
      onClose();
    } catch {
      setError('보충 일정 등록에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 px-4 py-8">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4 md:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-calendar-schedule-line"></i>
            </span>
            <div>
              <h2 className="text-base font-bold text-foreground-950">보충 일정 빠른 등록</h2>
              <p className="text-xs text-foreground-500">학생과 일자만 고르면 바로 잡혀요.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition hover:bg-background-200"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {students.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12">
            <i className="ri-user-3-line text-2xl text-foreground-400"></i>
            <p className="text-sm text-foreground-600">등록된 학생이 없어요.</p>
            <p className="text-xs text-foreground-500">학생을 먼저 등록한 뒤 보충 일정을 잡아주세요.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-5 md:px-6">
            {error && (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="학생" required>
                <select
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  className={inputClass}
                >
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="보충 일자" required>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="보충 사유">
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className={inputClass}
                  >
                    {REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="메모" hint="보충 범위나 준비물 등을 적어두세요.">
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={200}
                    placeholder="예: 지난주 결석 보충 - 이차함수 그래프"
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
              >
                {saving ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-check-line"></i>
                )}
                일정 등록
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}